import React, { useState } from 'react';
import { Plus, Edit, Trash2, MapPin, Building, Truck, Package, CreditCard, User, Home, Mail } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { StateSelect, CountrySelect, CityInput } from '../geo/GeoSelects';

const AddressManagement = ({ customerId, addresses = [], onAddressesChange }) => {
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    type: 'BILLING',
    name: '',
    company: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    county: '',
    phone: '',
    email: '',
    attention: '',
    isDefault: false,
    notes: ''
  });

  // Must match the backend AddressType enum — values not in the enum are
  // silently coerced to OTHER on save, losing the user's choice.
  const addressTypes = [
    { value: 'BILLING', label: 'Billing Address', icon: CreditCard, color: 'bg-blue-100 text-blue-800' },
    { value: 'SHIPPING', label: 'Shipping Address', icon: Truck, color: 'bg-green-100 text-green-800' },
    { value: 'DELIVERY', label: 'Delivery Address', icon: Package, color: 'bg-orange-100 text-orange-800' },
    { value: 'CORPORATE', label: 'Corporate / Headquarters', icon: Building, color: 'bg-gray-100 text-gray-800' },
    { value: 'BRANCH', label: 'Branch Office', icon: Home, color: 'bg-pink-100 text-pink-800' },
    { value: 'WAREHOUSE', label: 'Warehouse', icon: Package, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'OTHER', label: 'Other', icon: MapPin, color: 'bg-gray-100 text-gray-800' }
  ];

  const resetForm = () => {
    setAddressForm({
      type: 'BILLING',
      name: '',
      company: '',
      street: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      county: '',
      phone: '',
      email: '',
      attention: '',
      isDefault: false,
      notes: ''
    });
  };

  const handleAddAddress = () => {
    if (!addressForm.street || !addressForm.city || !addressForm.state || !addressForm.zipCode) {
      toast.error('Street, city, state, and ZIP code are required');
      return;
    }

    const newAddress = {
      id: `temp-${Date.now()}`,
      customerId,
      ...addressForm,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    onAddressesChange([...addresses, newAddress]);
    resetForm();
    setIsAddingAddress(false);
    toast.success('Address added successfully');
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      type: address.type,
      name: address.name || '',
      company: address.company || '',
      street: address.street,
      street2: address.street2 || '',
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country || 'USA',
      county: address.county || '',
      phone: address.phone || '',
      email: address.email || '',
      attention: address.attention || '',
      isDefault: address.isDefault,
      notes: address.notes || ''
    });
  };

  const handleUpdateAddress = () => {
    if (!addressForm.street || !addressForm.city || !addressForm.state || !addressForm.zipCode) {
      toast.error('Street, city, state, and ZIP code are required');
      return;
    }

    const updatedAddresses = addresses.map(address => {
      if (address.id === editingAddress.id) {
        return {
          ...address,
          ...addressForm,
          updatedAt: new Date()
        };
      }
      return address;
    });

    onAddressesChange(updatedAddresses);
    setEditingAddress(null);
    resetForm();
    toast.success('Address updated successfully');
  };

  const handleDeleteAddress = (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      const updatedAddresses = addresses.filter(address => address.id !== addressId);
      onAddressesChange(updatedAddresses);
      toast.success('Address deleted successfully');
    }
  };

  const getAddressTypeIcon = (type) => {
    const typeInfo = addressTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.icon : MapPin;
  };

  const getAddressTypeColor = (type) => {
    const typeInfo = addressTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getAddressTypeLabel = (type) => {
    const typeInfo = addressTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.label : type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <MapPin size={20} />
          Address Management
        </h3>
        <button type="button"          onClick={() => setIsAddingAddress(true)}
          className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
          title="Add address"
        >
          <Plus size={16} />
          Add Address
        </button>
      </div>

      {/* Add/Edit Address Form */}
      {(isAddingAddress || editingAddress) && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingAddress ? 'Edit Address' : 'Add New Address'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Type *</label>
              <select
                value={addressForm.type}
                onChange={(e) => setAddressForm(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {addressTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={addressForm.name}
                onChange={(e) => setAddressForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={addressForm.company}
                onChange={(e) => setAddressForm(prev => ({ ...prev, company: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attention</label>
              <input
                type="text"
                value={addressForm.attention}
                onChange={(e) => setAddressForm(prev => ({ ...prev, attention: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
              <input
                type="text"
                value={addressForm.street}
                onChange={(e) => setAddressForm(prev => ({ ...prev, street: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address 2</label>
              <input
                type="text"
                value={addressForm.street2}
                onChange={(e) => setAddressForm(prev => ({ ...prev, street2: e.target.value }))}
                placeholder="Suite, Unit, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <CityInput
                value={addressForm.city}
                state={addressForm.state}
                onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <StateSelect
                value={addressForm.state}
                onChange={(e) => setAddressForm(prev => ({ ...prev, state: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
              <input
                type="text"
                value={addressForm.zipCode}
                onChange={(e) => setAddressForm(prev => ({ ...prev, zipCode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <CountrySelect
                value={addressForm.country}
                onChange={(e) => setAddressForm(prev => ({ ...prev, country: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
              <input
                type="text"
                value={addressForm.county}
                onChange={(e) => setAddressForm(prev => ({ ...prev, county: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={addressForm.phone}
                onChange={(e) => setAddressForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={addressForm.email}
                onChange={(e) => setAddressForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={addressForm.isDefault}
                onChange={(e) => setAddressForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Set as default address for this type</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={addressForm.notes}
              onChange={(e) => setAddressForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button"              onClick={() => {
                setIsAddingAddress(false);
                setEditingAddress(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              title="Cancel"
            >
              Cancel
            </button>
            <button type="button"              onClick={editingAddress ? handleUpdateAddress : handleAddAddress}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
              title="Save address"
            >
              {editingAddress ? 'Update Address' : 'Add Address'}
            </button>
          </div>
        </div>
      )}

      {/* Addresses List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {addresses.map((address) => {
                const Icon = getAddressTypeIcon(address.type);
                return (
                  <tr key={address.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getAddressTypeColor(address.type)}`}>
                          <Icon size={12} />
                          {getAddressTypeLabel(address.type)}
                        </span>
                        {address.isDefault && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {address.name && (
                          <div className="text-sm font-medium text-gray-900">{address.name}</div>
                        )}
                        {address.company && (
                          <div className="text-sm text-gray-600">{address.company}</div>
                        )}
                        {address.attention && (
                          <div className="text-xs text-gray-500">Attn: {address.attention}</div>
                        )}
                        {address.phone && (
                          <div className="text-xs text-gray-500">📞 {address.phone}</div>
                        )}
                        {address.email && (
                          <div className="text-xs text-gray-500">✉️ {address.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div>{address.street}</div>
                        {address.street2 && <div>{address.street2}</div>}
                        <div>{address.city}, {address.state} {address.zipCode}</div>
                        {address.county && <div className="text-gray-500">{address.county} County</div>}
                        <div className="text-gray-500">{address.country}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        address.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {address.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button type="button"                          onClick={() => handleEditAddress(address)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Address"
                        >
                          <Edit size={16} />
                        </button>
                        <button type="button"                          onClick={() => handleDeleteAddress(address.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Address"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {addresses.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MapPin size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">No addresses found</p>
            <p className="text-gray-500">Add your first address to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressManagement;
